// Thin fetch wrapper that:
//  - Auto-attaches Authorization: Bearer <accessToken> from localStorage.
//  - On any 401 response, attempts a single refresh via POST /api/auth/refresh.
//    If refresh succeeds, replays the original request once with the new token.
//    If refresh fails, clears tokens, fires `ss.auth.signedOut` event, and lets
//    the 401 propagate so callers can react.
//  - Single-flight: if a refresh is already in-flight, queue subsequent 401s on
//    the same promise so multiple parallel calls don't trigger N refreshes.
//
// Tokens are stored under `ss.auth.access` and `ss.auth.refresh` in
// localStorage. Use the `ss.auth.signedOut` window event to react to forced
// sign-outs (e.g. AuthContext clears its in-memory state).

import { API_BASE } from '@/env';

export const ACCESS_TOKEN_KEY = 'ss.auth.access';
export const REFRESH_TOKEN_KEY = 'ss.auth.refresh';
export const SIGNED_OUT_EVENT = 'ss.auth.signedOut';

/**
 * Fired when the server answers 402 Payment Required — the request was fine, it just exceeds
 * the caller's plan. Broadcast rather than thrown so a single listener can show one upgrade
 * prompt: quota refusals arrive from ~20 different services, most of which stringify the whole
 * response body into an Error message and lose the status code entirely.
 *
 * `detail` carries `{ error, upgradeHint }` from the API. The original 402 Response is still
 * returned to the caller, so existing per-screen error handling keeps working unchanged.
 */
export const QUOTA_EXCEEDED_EVENT = 'ss.plan.quotaExceeded';

export interface QuotaExceededDetail {
  error: string;
  upgradeHint?: string | null;
}

export interface RefreshResponse {
  user: {
    id: string;
    email: string;
    roles: string[];
    tier: string;
  planId: string;
  planName: string;
  planRank: number;
  };
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Single-flight refresh promise. Any call that sees a 401 while another refresh
// is in-flight will await this same promise instead of firing its own refresh.
let inFlightRefresh: Promise<string | null> | null = null;

function readAccess(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function readRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* swallow */
  }
}

function fireSignedOut() {
  try {
    window.dispatchEvent(new CustomEvent(SIGNED_OUT_EVENT));
  } catch {
    /* swallow */
  }
}

function isAbsoluteUrl(input: RequestInfo | URL): boolean {
  if (typeof input !== 'string') return true; // Request / URL: assume absolute or already-built
  return /^https?:\/\//i.test(input);
}

function withAuthHeader(init: RequestInit | undefined, token: string | null): RequestInit {
  const headers = new Headers(init?.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  } else {
    headers.delete('Authorization');
  }
  return { ...init, headers };
}

/**
 * Attempt to refresh the access token. Returns the new access token on
 * success, or null on failure. On failure clears tokens and fires the
 * signed-out event. Single-flighted: concurrent callers share one promise.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  const refresh = readRefresh();
  if (!refresh) {
    clearTokens();
    fireSignedOut();
    return null;
  }

  inFlightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) {
        clearTokens();
        fireSignedOut();
        return null;
      }
      const body: RefreshResponse = await res.json();
      try {
        localStorage.setItem(ACCESS_TOKEN_KEY, body.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, body.refreshToken);
      } catch {
        /* swallow */
      }
      return body.accessToken;
    } catch {
      clearTokens();
      fireSignedOut();
      return null;
    } finally {
      // Clear AFTER the awaiting callers have read it.
      // Use microtask deferral so awaiters resolved synchronously still see it.
      setTimeout(() => {
        inFlightRefresh = null;
      }, 0);
    }
  })();

  return inFlightRefresh;
}

/**
 * Auth-aware fetch. Drop-in replacement for `window.fetch` that auto-attaches
 * the bearer token and transparently refreshes once on 401.
 *
 * Skips both the auth-header attach AND the refresh-on-401 dance ONLY for the
 * three bootstrap endpoints — `/api/auth/login`, `/api/auth/register`,
 * `/api/auth/refresh` — which are public and would create a refresh loop if
 * they 401'd. Everything else under `/api/auth/*` (notably `/me` and
 * `/logout`) gets the bearer and the normal refresh-on-401 flow.
 */
const AUTH_BOOTSTRAP_RE = /\/api\/auth\/(login|register|refresh)(?:[?/]|$)/;

export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = typeof input === 'string' ? input : input.toString();
  const isBootstrap = AUTH_BOOTSTRAP_RE.test(url);

  const token = isBootstrap ? null : readAccess();
  let res = await fetch(input, withAuthHeader(init, token));

  if (res.status === 402) {
    await announceQuotaExceeded(res);
    return res;
  }

  if (res.status !== 401 || isBootstrap) {
    return res;
  }

  // 401 on a normal endpoint → try one refresh, then replay once.
  const newToken = await refreshAccessToken();
  if (!newToken) {
    return res; // let the 401 propagate
  }

  res = await fetch(input, withAuthHeader(init, newToken));
  if (res.status === 402) await announceQuotaExceeded(res);
  return res;
}

/**
 * Read the 402 body and broadcast it, without consuming the caller's copy — `res.clone()`
 * matters because the caller almost always reads the body too, and a Response body can only
 * be read once.
 */
async function announceQuotaExceeded(res: Response): Promise<void> {
  let detail: QuotaExceededDetail = { error: 'This action exceeds your plan.' };
  try {
    const body = await res.clone().json();
    detail = { error: body?.error ?? detail.error, upgradeHint: body?.upgradeHint ?? null };
  } catch {
    // A 402 with an unreadable body still deserves a prompt — fall back to the default text.
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<QuotaExceededDetail>(QUOTA_EXCEEDED_EVENT, { detail }));
  }
}

export const _internal = {
  refreshAccessToken,
  readAccess,
  readRefresh,
  clearTokens,
  isAbsoluteUrl,
};
